import { DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import useAssignRole from 'api/users/useAssignRole/useAssignRole';
import useUserSelectedRoleList from 'api/users/useUserSelectedRoleList/useUserSelectedRoleList';
import PageSpinner from 'components/shared/pageSpinner/PageSpinner';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import useRolePermissionTableColumns from 'hooks/accessControl/useRolePermissionTableColumns';
import { useEffect, useRef, useState } from 'react';

const UserRolesModal = ({
  open,
  handleClose,
  editLoading,
  userGuid,
  onAssignRoleSuccess,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [selectedRows, setSelectedRows] = useState([]);

  const tableRef = useRef(null);
  const previousSelectedRowsRef = useRef([]);
  const isAssigningRef = useRef(false);

  const {
    getSelectedRoleList,
    selectedRoleListLoading,
    selectedRoleList,
    pageRef,
  } = useUserSelectedRoleList();

  const { assignRoleRequest, assignRoleLoading } = useAssignRole();

  const [columnsData] = useRolePermissionTableColumns(tableRef);

  const fetchRoles = (pageNumber = page, count = pageSize) => {
    if (!userGuid) return;

    getSelectedRoleList({
      Guid: userGuid,
      Page: pageNumber,
      Count: count,
    });
  };

  useEffect(() => {
    if (!open || !userGuid) return;

    setPage(1);
    setSelectedRows([]);
    previousSelectedRowsRef.current = [];
    isAssigningRef.current = false;
    fetchRoles(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userGuid]);

  useEffect(() => {
    if (selectedRoleListLoading) return;

    const selected = selectedRoleList
      .filter(role => role.selected)
      .map(role => role.id);

    previousSelectedRowsRef.current = selected;
    setSelectedRows(selected);
  }, [selectedRoleListLoading, selectedRoleList]);

  const handleSelectedRowsChange = rows => {
    if (isAssigningRef.current || assignRoleLoading) {
      setSelectedRows([...previousSelectedRowsRef.current]);
      return;
    }

    const previous = previousSelectedRowsRef.current;
    const newlyAdded = rows.filter(id => !previous.includes(id));
    const newlyRemoved = previous.filter(id => !rows.includes(id));
    const roleGuid = newlyAdded[0] ?? newlyRemoved[0];

    if (!roleGuid || !userGuid) {
      setSelectedRows(previous);
      return;
    }

    const nextSelected = newlyAdded.includes(roleGuid)
      ? [...previous, roleGuid]
      : previous.filter(id => id !== roleGuid);

    isAssigningRef.current = true;
    setSelectedRows(nextSelected);

    assignRoleRequest({ roleGuid, userGuid }, () => {
      previousSelectedRowsRef.current = nextSelected;
      isAssigningRef.current = false;
      onAssignRoleSuccess?.();
    });
  };

  const handlePageChange = pageNumber => {
    setPage(pageNumber);
    fetchRoles(pageNumber);
  };

  const handlePageSizeChange = value => {
    setPageSize(value);
    setPage(1);
    fetchRoles(1, value);
  };

  const handleSearch = () => {
    setPage(1);
    fetchRoles(1);
  };

  const isBusy = editLoading || assignRoleLoading || selectedRoleListLoading;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      sx={{ direction: 'rtl' }}
    >
      <DialogTitle>{pageRef?.refTitle}</DialogTitle>

      <DialogContent>
        <ApiPaginateTable
          columns={columnsData}
          rowsData={selectedRoleList}
          rows={selectedRoleList}
          pageSize={pageSize}
          isLoading={isBusy}
          setPageSize={handlePageSizeChange}
          apiRef={tableRef}
          onPageChange={handlePageChange}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef?.totalCount / pageSize) : 1
          }
          handleSearchApi={handleSearch}
          setSelectedRows={handleSelectedRowsChange}
          selectedRows={selectedRows}
          disableSelectAll
        />
      </DialogContent>

      <DialogActions sx={{ gap: 1, padding: 3 }}>
        <Button onClick={handleClose} variant="contained" disabled={isBusy}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserRolesModal;
